import { useCallback, useEffect, useState } from 'react'
import { tradingClient } from '../../api'
import type { SpotDefaultConfig } from '../../api/tradingTypes'

export function readLastExchangeId(): string {
  try {
    return localStorage.getItem('lastExchangeId') || ''
  } catch {
    return ''
  }
}

function rememberExchangeId(exchangeId: string) {
  try {
    localStorage.setItem('lastExchangeId', exchangeId)
    localStorage.setItem('lastTradingType', 'spot')
  } catch {
    /* storage unavailable — selection just is not remembered */
  }
}

/**
 * The default-config list both settings pages load with `checkExchange=1`
 * (`V3SpotConfigService.searchDefaultConfigs(true)`), plus the selected
 * exchange tab. Selection mirrors `refreshData()` + `V3ExchangeService`:
 * the remembered `lastExchangeId` wins when it is configured, else the first
 * configured exchange; every explicit choice is remembered again.
 */
export function useDefaultConfigs() {
  const [configs, setConfigs] = useState<SpotDefaultConfig[] | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [exchangeId, setExchangeIdState] = useState('')
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    tradingClient
      .searchDefaultConfigs(true)
      .then((result) => {
        if (cancelled) return
        const list = Array.isArray(result) ? result : []
        setConfigs(list)
        setError(null)
        setExchangeIdState((current) => {
          const preferred = current || readLastExchangeId()
          if (preferred && list.some((c) => c.exchangeId === preferred)) return preferred
          return list[0]?.exchangeId ?? ''
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setConfigs([])
        setError(err instanceof Error ? err : new Error('Could not load the configuration'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadToken])

  const setExchangeId = useCallback((value: string) => {
    setExchangeIdState(value)
    if (value) rememberExchangeId(value)
  }, [])

  const reload = useCallback((preferExchangeId?: string) => {
    if (preferExchangeId) setExchangeIdState(preferExchangeId)
    setReloadToken((n) => n + 1)
  }, [])

  return { configs, error, loading, exchangeId, setExchangeId, reload }
}

/** Legacy key-status icon (`fa-check text-success` / `fa-times text-danger`):
 * both key and secret must be stored. */
export function keyIsComplete(config: SpotDefaultConfig): boolean {
  return Number(config.exchangeKey?.key) === 1 && Number(config.exchangeKey?.secret) === 1
}
